'use client';

import React, { useState, Suspense } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, ArrowLeft, Gift, Zap, Crown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import { toast } from 'sonner';

function RegisterForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const planParam = searchParams.get('plan');
  const cycleParam = searchParams.get('cycle');
  const { signUp, signInWithGoogle } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Build post-registration redirect URL
  const getPostRegisterRedirect = () => {
    if (!planParam || planParam === 'free') return '/';
    const checkoutPlan = planParam === 'pro_max_lifetime' ? 'pro_max' :
                         planParam === 'pro_lifetime' ? 'pro' :
                         planParam; // 'pro' or 'pro_max'
    const cycle = cycleParam || 'monthly';
    return `/api/stripe/checkout?plan=${checkoutPlan}&cycle=${cycle}`;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await signUp(email, password, { fullName });
      toast.success('Account created! Welcome to WealthOS.');

      // Send signup confirmation email
      try {
        await fetch('/api/email/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'signup_confirmation',
            to: email,
            name: fullName || email.split('@')[0],
          }),
        });
      } catch {
        // Non-blocking — don't fail registration if email fails
      }

      // Redirect to financial quiz first, then to plan checkout or dashboard
      const postQuizRedirect = (!planParam || planParam === 'free')
        ? '/'
        : `/api/stripe/checkout?plan=${planParam === 'pro_max_lifetime' ? 'pro_max' : planParam === 'pro_lifetime' ? 'pro' : planParam}&cycle=${cycleParam || 'monthly'}`;
      router.push(`/financial-quiz?redirect=${encodeURIComponent(postQuizRedirect)}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Registration failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Google sign-in failed';
      toast.error(msg);
      setGoogleLoading(false);
    }
  };

  // Determine highlighted plan from query params for the banner
  const isProMax = planParam === 'pro_max' || planParam === 'pro_max_lifetime';
  const isPro = planParam === 'pro' || planParam === 'pro_lifetime';
  const isLifetime = planParam === 'pro_lifetime' || planParam === 'pro_max_lifetime';

  return (
    <div className="min-h-screen bg-background mesh-bg dark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <button
          onClick={() => router.back()}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <AppLogo size={48} />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Create your account</h1>
          <p className="text-sm text-muted-foreground mt-1">Start your wealth journey today</p>
        </div>

        {/* Plan highlight banner — shows selected plan if coming from pricing */}
        {(isPro || isProMax) ? (
          <div
            className="rounded-2xl p-4 border mb-4"
            style={isProMax
              ? { background: 'rgba(201,162,39,0.08)', borderColor: 'rgba(201,162,39,0.4)' }
              : { background: 'rgba(var(--primary-rgb),0.05)', borderColor: 'rgba(var(--primary-rgb),0.4)' }}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                style={isProMax ? { background: 'rgba(201,162,39,0.2)' } : {}}
              >
                {isProMax ? <Crown size={18} style={{ color: '#C9A227' }} /> : <Zap size={18} className="text-primary" />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-foreground mb-0.5">
                  {isLifetime
                    ? `🏆 ${isProMax ? 'Pro Max' : 'Pro'} Lifetime — One-time payment`
                    : `🎯 ${isProMax ? 'Pro Max' : 'Pro'} Plan selected${cycleParam && cycleParam !== 'monthly' ? ` · ${cycleParam}` : ''}`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isLifetime
                    ? 'Create your account to complete your lifetime purchase.'
                    : 'Create your account to proceed to checkout with your selected plan.'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Default free trial highlight */
          <div className="glass-card rounded-2xl p-4 border border-primary/40 bg-primary/5 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Gift size={18} className="text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-foreground mb-0.5">🎁 7-Day Free Trial — No Credit Card Needed</p>
                <p className="text-xs text-muted-foreground mb-2">Create your account and unlock a full Pro or Pro Max trial instantly.</p>
                <div className="flex gap-2">
                  <span className="flex items-center gap-1 text-[11px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                    <Zap size={10} /> Pro — $4.99/mo
                  </span>
                  <span className="flex items-center gap-1 text-[11px] font-medium text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded-full">
                    <Crown size={10} /> Pro Max — $12.99/mo
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="glass-card rounded-2xl p-6 border border-border">
          {/* Google Sign In */}
          <button
            onClick={handleGoogle}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-border bg-muted hover:bg-border text-foreground font-medium text-sm transition-all duration-200 active:scale-95 mb-4 disabled:opacity-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            {googleLoading ? 'Connecting...' : 'Continue with Google'}
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Full Name</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your full name"
                  required
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-muted border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-muted border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  required
                  className="w-full pl-9 pr-10 py-2.5 rounded-xl bg-muted border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Confirm Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your password"
                  required
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-muted border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl gold-gradient text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity active:scale-95 disabled:opacity-50"
            >
              {loading ? 'Creating account...' : (
                <>Create Account <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-muted-foreground mt-4">
            Already have an account?{' '}
            <Link href="/login" className="text-primary hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function RegisterPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <RegisterForm />
    </Suspense>
  );
}
